"use strict";
/*
 * This file is part of the IVMS Online.
 *
 * @copyright 2026 © by Rafał Wrzeszcz - Wrzasq.pl.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.VesselStatusPage = void 0;
const material_1 = require("@mui/material");
const Cancel_1 = require("@mui/icons-material/Cancel");
const CheckCircle_1 = require("@mui/icons-material/CheckCircle");
const moment = require("moment");
const React = require("react");
const api_1 = require("../../api");
const useVessel_1 = require("../ui/useVessel");
const OnlineStatus = ({ isOnline }) => {
    return isOnline
        ? React.createElement(material_1.Chip, { sx: { alignSelf: "flex-start" }, label: "Online", color: "success" })
        : React.createElement(material_1.Chip, { sx: { alignSelf: "flex-start" }, label: "Offline", color: "error" });
};
const NonEmptyArray = ({ caption, data, headers, producer }) => {
    if (data.length == 0) {
        return React.createElement(React.Fragment, null);
    }
    return React.createElement(React.Fragment, null,
        React.createElement(material_1.Typography, { variant: "h5" }, caption),
        React.createElement(material_1.TableContainer, { component: material_1.Paper },
            React.createElement(material_1.Table, { sx: { minWidth: 650 }, "aria-label": "simple table" },
                React.createElement(material_1.TableHead, null,
                    React.createElement(material_1.TableRow, null, headers.map(header => React.createElement(material_1.TableCell, { key: header }, header)))),
                React.createElement(material_1.TableBody, null, data.map(record => producer(record))))));
};
const AlarmsTable = ({ data }) => React.createElement(NonEmptyArray, { caption: "Active alarms", data: data, headers: ["ID", "Name", "Since", "Acknowledged?"], producer: (record) => {
        const since = moment(record.since);
        return React.createElement(material_1.TableRow, { key: record.id },
            React.createElement(material_1.TableCell, null, record.id),
            React.createElement(material_1.TableCell, null, record.name),
            React.createElement(material_1.TableCell, null,
                since.format("DD.MM.YYYY"),
                React.createElement("br", null),
                since.format("HH:mm:ss")),
            React.createElement(material_1.TableCell, null, record.acknowledged
                ? React.createElement(CheckCircle_1.default, { color: "success" })
                : React.createElement(Cancel_1.default, { color: "error" })));
    } });
const MaintsTable = ({ data }) => React.createElement(NonEmptyArray, { caption: "Maintenance", data: data, headers: ["ID", "Name", "Due in"], producer: (record) => React.createElement(material_1.TableRow, { key: record.id },
        React.createElement(material_1.TableCell, null, record.id),
        React.createElement(material_1.TableCell, null, record.name),
        React.createElement(material_1.TableCell, null, record.dueIn)) });
const SensorsDefinitionList = ({ sensors }) => {
    const entries = sensors.map(sensor => {
        return React.createElement(React.Fragment, { key: sensor.key },
            React.createElement("dt", null, sensor.title),
            React.createElement("dd", null, sensor.pattern.replace("%value%", sensor.value)));
    });
    return React.createElement("dl", null, entries);
};
const SensorsTable = ({ sensors, table }) => {
    const rowSize = table.captions.length;
    const chunks = sensors.reduce((results, sensor, index) => {
        const chunk = Math.floor(index / rowSize);
        if (!results[chunk]) {
            results[chunk] = [];
        }
        results[chunk].push(sensor);
        return results;
    }, []);
    const rows = chunks.map((chunk, index) => {
        const row = chunk.map(sensor => React.createElement(material_1.TableCell, { key: sensor.key }, sensor.pattern.replace("%value%", sensor.value)));
        // first cell of each row takes title as a series label
        return React.createElement(material_1.TableRow, { key: index },
            React.createElement(material_1.TableCell, null, chunk[0].title),
            row);
    });
    return React.createElement(material_1.TableContainer, { component: material_1.Paper },
        React.createElement(material_1.Table, { sx: { width: "100%" }, "aria-label": "simple table" },
            React.createElement(material_1.TableHead, null,
                React.createElement(material_1.TableRow, null,
                    React.createElement(material_1.TableCell, null),
                    table.captions.map(header => React.createElement(material_1.TableCell, { key: header }, header)))),
            React.createElement(material_1.TableBody, null, rows)));
};
const VesselStatusPage = () => {
    const vessel = (0, useVessel_1.useVessel)(api_1.loadVesselStatusPage);
    if (!vessel) {
        return React.createElement(material_1.CircularProgress, null);
    }
    const sections = vessel.status.details.sections.map(section => {
        let content = React.createElement(React.Fragment, null);
        if (section.table) {
            content = React.createElement(SensorsTable, { sensors: section.sensors, table: section.table });
        }
        else {
            content = React.createElement(SensorsDefinitionList, { sensors: section.sensors });
        }
        return React.createElement(material_1.Card, { key: section.title, sx: {
                width: {
                    // on mobile - full width
                    sm: "100%",
                    // bigger screens - two in a row
                    md: "calc(50% - 8px)",
                    // large screens - even three will match
                    lg: "calc(32% - 8px)"
                },
            } },
            React.createElement(material_1.CardHeader, { title: section.title }),
            React.createElement(material_1.CardContent, null, content));
    });
    vessel.status.details.alarms.sort((left, right) => left.since - right.since);
    vessel.status.details.maintenance.sort((left, right) => left.id < right.id ? -1 : 1);
    return React.createElement(material_1.Stack, { spacing: 3 },
        React.createElement(OnlineStatus, { isOnline: vessel.status.isOnline }),
        React.createElement(AlarmsTable, { data: vessel.status.details.alarms }),
        React.createElement(MaintsTable, { data: vessel.status.details.maintenance }),
        React.createElement(material_1.Box, { sx: {
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
            } }, sections));
};
exports.VesselStatusPage = VesselStatusPage;
